import type { PageType, ThemeKind } from '../registry';

// The page a merchant writes in the admin — Terms, a privacy notice, an about page. What it holds is
// what they typed and nothing about how it looks: the shop's theme decides that, and the page the
// merchant's words are generated into reads the theme's own tokens.

/** A merchant-authored page as a theme renders it: what it says, and nothing about how it looks. */
export type StoreContentPage = { title: string; bodyHtml: string };

/**
 * The page type merchant-authored content renders on, and the theme kind it means that on. `article`
 * is overloaded in the shared vocabulary: to a `store` theme it is the page the merchant wrote, to a
 * `funnel` or `landing` theme it is an advertorial with a hero, reviews and an offer. Binding a Terms
 * document onto an advertorial would serve the theme's own copy as the merchant's legal text, so only
 * a store theme's article page is treated as theirs.
 */
export const CONTENT_PAGE: PageType = 'article';
export const CONTENT_KIND: ThemeKind = 'store';

/** One stored section of the admin's page editor, narrowed to the single field it keeps prose in. */
type StoredSection = { settings?: { bodyHtml?: unknown } } | null | undefined;

/** The CMS row `GET /tenant/pages/:slug` answers with, before it is narrowed. */
export type RawContentPage = {
  title?: unknown;
  bodyHtml?: unknown;
  sectionsData?: { sections?: Record<string, StoredSection>; order?: unknown } | null;
};

// Reads one stored section's prose out of the page editor's untyped bag.
const sectionBody = (section: StoredSection): string =>
  typeof section?.settings?.bodyHtml === 'string' ? section.settings.bodyHtml : '';

// Joins every prose section the merchant's page holds, in the order the editor saved them.
function storedBody(raw: RawContentPage): string {
  const data = raw.sectionsData;
  if (!data) return '';
  const order = Array.isArray(data.order) ? data.order : [];
  return order
    .map((key) => sectionBody(data.sections?.[String(key)]))
    .filter((part) => part.trim() !== '')
    .join('\n');
}

// Narrows the CMS row into the two things a theme renders, or null for a page with nothing written.
export function readContentPage(raw: RawContentPage | null | undefined): StoreContentPage | null {
  if (!raw) return null;
  const title = typeof raw.title === 'string' ? raw.title : '';
  const bodyHtml = (typeof raw.bodyHtml === 'string' && raw.bodyHtml.trim() !== '') ? raw.bodyHtml : storedBody(raw);
  return title.trim() === '' && bodyHtml.trim() === '' ? null : { title, bodyHtml };
}
